import { Language } from '../../models/serializable/language.js';
import { VersionRange } from '../../models/core/version-range.js';
import {
  DataModelBuilder,
  isSameData,
  SerializationContext,
  SerializedData,
} from '../../models/dto/dto.js';
import { toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { RenderableComponent } from '../core/component.js';
import { LocalizedString } from '../../models/serializable/localized-string.js';
import { LocalizedPhotoReference } from '../../models/serializable/localized-photo-reference.js';

export class IntentComponent extends RenderableComponent {
  static readonly TYPE_ID: string = '9e7ca9c2836d4dee95b603e6e3b5235e';
  static readonly SCHEMA_VERSION: number = 5;
  static readonly DEFAULT_RENDER_ORDER: number = 1;

  constructor(
    public readonly titles: LocalizedString,
    public readonly descriptions: LocalizedString,
    public readonly photos: LocalizedPhotoReference,
    public readonly labels: LocalizedString,
    public readonly solver: string,
    public readonly renderOrder: number,
    public readonly isRenderingEnabled: boolean,
    public readonly overrideDefaultAction: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return IntentComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(4, IntentComponent.SCHEMA_VERSION);
  }

  get $removable(): boolean {
    return true;
  }

  static fromMap(context: SerializationContext, map: SerializedData): IntentComponent {
    return new IntentComponent(
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'titles')),
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'descriptions')),
        context.fromData<LocalizedPhotoReference>(MapUtils.get<SerializedData>(map, 'photos')),
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'labels')),
        MapUtils.get<string>(map, 'solver'),
        MapUtils.get<number>(map, 'renderOrder'),
        MapUtils.get<boolean>(map, 'isRenderingEnabled'),
        MapUtils.get<boolean>(map, 'overrideDefaultAction'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'titles': context.toData(this.titles),
      'descriptions': context.toData(this.descriptions),
      'photos': context.toData(this.photos),
      'labels': context.toData(this.labels),
      'solver': this.solver,
      'renderOrder': this.renderOrder,
      'isRenderingEnabled': this.isRenderingEnabled,
      'overrideDefaultAction': this.overrideDefaultAction,
    };
  }

  clone(): this {
    return new IntentComponent(
        this.titles.clone(),
        this.descriptions.clone(),
        this.photos.clone(),
        this.labels.clone(),
        this.solver,
        this.renderOrder,
        this.isRenderingEnabled,
        this.overrideDefaultAction,
    ) as this;
  }

  isSame(other: IntentComponent): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.titles, this.titles) &&
          isSameData(other.descriptions, this.descriptions) &&
          isSameData(other.photos, this.photos) &&
          isSameData(other.labels, this.labels) &&
          other.solver === this.solver &&
          other.renderOrder === this.renderOrder &&
          other.isRenderingEnabled === this.isRenderingEnabled &&
          other.overrideDefaultAction === this.overrideDefaultAction;
  }
}

export class IntentComponentBuilder extends DataModelBuilder<IntentComponent> {
  build(context: SerializationContext, map: SerializedData): IntentComponent {
    return IntentComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(IntentComponent.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(IntentComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the component was converted to `RenderableComponent`.
    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'renderOrder', IntentComponent.DEFAULT_RENDER_ORDER);
      MapUtils.setIfNullOrUndefined(map, 'isRenderingEnabled', true);
      version = 2;
    }

    // In this version parameter `overrideDefaultAction` of type `bool` was added.
    if(version < 3) {
      MapUtils.setIfNullOrUndefined(map, 'overrideDefaultAction', false);
      version = 3;
    }

    // In this version parameter `labels` was converted from type `Map<Language, String>` to type `LocalizedString`.
    if(version < 4) {
      const labels = MapUtils.getMap<Language, string>(map, 'labels', (k, v) => {
        return { key: toEnumFromString(k, Language), value: v as string };
      });

      map['labels'] = context.toData(LocalizedString.localized(labels));
      version = 4;
    }

    // In this version the the following parameters were added:
    // - `titles` of type `LocalizedString`
    // - `descriptions` of type `LocalizedString`
    // - `photos` of type `LocalizedPhotoReference`
    if(version < 5) {
      map['titles'] ??= context.toData(LocalizedString.empty());
      map['descriptions'] ??= context.toData(LocalizedString.empty());
      map['photos'] ??= context.toData(LocalizedPhotoReference.empty());
      version = 4;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
